import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, TrafficLayer, useJsApiLoader } from '@react-google-maps/api';
import { collection, getDocs, query, where, getDoc, doc, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { FaBus, FaMapMarkerAlt, FaTachometerAlt, FaBatteryFull, FaChevronDown, FaTimes, FaSearch, FaUser, FaRoute, FaClock } from 'react-icons/fa';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const libraries = ['places', 'marker'];

// Helper function to check if a trip is currently in session
const isTripInSession = (route, roster = null) => {
  if (!route || !route.time || route.time === 'N/A') {
    return false;
  }

  try {
    // Check roster date range if roster is provided
    if (roster) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Handle Firestore timestamp or ISO string
      let startDate = null;
      if (roster.date) {
        if (roster.date.toDate) {
          // Firestore timestamp
          startDate = roster.date.toDate();
        } else {
          // ISO string or regular date
          startDate = new Date(roster.date);
        }
        startDate.setHours(0, 0, 0, 0);
      }

      let endDate = null;
      if (roster.endDate) {
        if (roster.endDate.toDate) {
          // Firestore timestamp
          endDate = roster.endDate.toDate();
        } else {
          // ISO string or regular date
          endDate = new Date(roster.endDate);
        }
        endDate.setHours(23, 59, 59, 999);
      }

      // Check if today is within the roster date range
      if (startDate && today < startDate) {
        return false; // Trip hasn't started yet
      }

      if (endDate && today > endDate) {
        return false; // Trip has ended
      }

      // Check frequency-based day matching
      const frequency = roster.frequency || route.rosterFrequency || 'daily';
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      if (frequency === 'once') {
        // For one-time rosters, check if today matches the start date exactly
        if (startDate && today.getTime() !== startDate.getTime()) {
          return false;
        }
      } else if (frequency === 'weekdays') {
        // For weekday rosters, check if today is a weekday (Monday-Friday)
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          return false; // It's a weekend
        }

        // Also check if specific weekdays are set
        if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
          // Convert dayOfWeek to match roster.weekdays format (1 = Monday, 7 = Sunday)
          const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
          if (!roster.weekdays.includes(rosterDayOfWeek)) {
            return false;
          }
        }
      }
      // For 'daily' frequency, any day within the date range is valid
    }

    // Parse the route time (format: "HH:MM" or "HH:MM:SS")
    const timeParts = route.time.split(':');
    if (timeParts.length < 2) {
      return false;
    }

    const routeHours = parseInt(timeParts[0], 10);
    const routeMinutes = parseInt(timeParts[1], 10);

    if (isNaN(routeHours) || isNaN(routeMinutes)) {
      return false;
    }

    // Get current date and time
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    // Calculate route start time in minutes
    const routeStartTimeInMinutes = routeHours * 60 + routeMinutes;

    // Get total duration with wait time (in seconds)
    const totalDurationSeconds = route.routeMetrics?.totalDurationWithWait || 0;
    if (totalDurationSeconds === 0) {
      return false;
    }

    // Calculate route end time in minutes
    const routeEndTimeInMinutes = routeStartTimeInMinutes + Math.ceil(totalDurationSeconds / 60);

    // Check if current time falls within the trip window
    // Allow a small buffer (5 minutes before start) to account for early departures
    const bufferMinutes = 5;
    const adjustedStartTime = routeStartTimeInMinutes - bufferMinutes;

    // Check if current time is within the trip window
    if (currentTimeInMinutes >= adjustedStartTime && currentTimeInMinutes <= routeEndTimeInMinutes) {
      return true;
    }

    // Also check if we're in the next day's window (for trips that span midnight)
    // This handles cases where a trip starts late at night and ends early morning
    if (routeEndTimeInMinutes > 24 * 60) {
      const nextDayEndTime = routeEndTimeInMinutes - (24 * 60);
      if (currentTimeInMinutes <= nextDayEndTime) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking if trip is in session:', error);
    return false;
  }
};

// Create Font Awesome bus icon as Google Maps marker (dark color)
const createBusIcon = (size = 32) => {
  // Font Awesome 5 bus icon (actual icon path)
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
      <path d="M256 0C390.4 0 480 35.2 480 80V96l0 32c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32l0 160c0 17.7-14.3 32-32 32v32c0 17.7-14.3 32-32 32H384c-17.7 0-32-14.3-32-32V448H160v32c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32l0-32c-17.7 0-32-14.3-32-32l0-160c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h0V96h0V80C32 35.2 121.6 0 256 0zM96 160v96c0 17.7 14.3 32 32 32H240V128H128c-17.7 0-32 14.3-32 32zM272 288H384c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H272V288zM112 400c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zm288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zM352 80c0-8.8-7.2-16-16-16H176c-8.8 0-16 7.2-16 16s7.2 16 16 16H336c8.8 0 16-7.2 16-16z" fill="#1a1a1a" stroke="#000000" stroke-width="1"/>
    </svg>
  `;
  
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
};

const toTimestampMs = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const hasMeaningfulMovement = (prevPoint, nextPoint, minDelta = 0.0001) => {
  if (!prevPoint) return true;
  return (
    Math.abs(prevPoint.position.lat - nextPoint.position.lat) >= minDelta ||
    Math.abs(prevPoint.position.lng - nextPoint.position.lng) >= minDelta
  );
};

const calculateBearing = (from, to) => {
  if (!from || !to) return 0;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLon = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

const trackedRadarEvents = [
  { key: 'harsh_braking', label: 'Harsh Braking', color: '#86efac' },
  { key: 'harsh_acceleration', label: 'Harsh Acceleration', color: '#93c5fd' },
  { key: 'overspeeding', label: 'Overspeeding', color: '#fca5a5' },
  { key: 'sustained_speeding', label: 'Sustained Speeding', color: '#c4b5fd' }
];

const getEmptyEventCounts = () => ({
  harsh_braking: 0,
  harsh_acceleration: 0,
  overspeeding: 0,
  sustained_speeding: 0
});

const mapEventNameToRadarKey = (eventNameRaw) => {
  const normalized = String(eventNameRaw || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!normalized) return null;

  const hasHarsh = normalized.includes('harsh');
  const hasBrake = normalized.includes('brak');
  const hasAccel = normalized.includes('acceler') || normalized.includes('accel');
  const hasSustained = normalized.includes('sustained');
  const hasSpeeding = normalized.includes('speeding') || normalized.includes('overspeed') || normalized.includes('over speed');

  if (hasHarsh && hasBrake) return 'harsh_braking';
  if (hasHarsh && hasAccel) return 'harsh_acceleration';
  if (hasSustained && hasSpeeding) return 'sustained_speeding';
  if (hasSpeeding) return 'overspeeding';

  // Fallback for exact-style keys stored directly in events.
  const exactKey = normalized.replace(/\s+/g, '_');
  if (Object.prototype.hasOwnProperty.call(getEmptyEventCounts(), exactKey)) {
    return exactKey;
  }

  return null;
};

const createRadarDataFromCounts = (counts) => trackedRadarEvents.map((trackedEvent) => ({
  event: trackedEvent.label,
  value: counts[trackedEvent.key] || 0,
  color: trackedEvent.color
}));

const createSpeedHistogramData = (speedEvents, timeBatchMinutes = 5) => {
  if (!Array.isArray(speedEvents) || speedEvents.length === 0) return [];

  const validEvents = speedEvents
    .filter((item) => Number.isFinite(item.speed) && item.speed >= 0 && Number.isFinite(item.timestampMs))
    .sort((a, b) => a.timestampMs - b.timestampMs);
  if (validEvents.length === 0) return [];

  // Batch events by time window first to reduce noisy point density.
  const batchMs = timeBatchMinutes * 60 * 1000;
  const timeBatches = new Map();
  validEvents.forEach((item) => {
    const batchKey = Math.floor(item.timestampMs / batchMs);
    if (!timeBatches.has(batchKey)) {
      timeBatches.set(batchKey, []);
    }
    timeBatches.get(batchKey).push(item.speed);
  });

  return Array.from(timeBatches.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([batchKey, batchSpeeds]) => {
      const batchStartMs = batchKey * batchMs;
      const averageSpeed = batchSpeeds.reduce((sum, speed) => sum + speed, 0) / batchSpeeds.length;
      return {
        timeLabel: new Date(batchStartMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        avgSpeed: Number(averageSpeed.toFixed(1))
      };
    });
};

const historyWindowOptions = [3, 6, 12, 24];

const Track = () => {
  // Define constants before state declarations
  const defaultCenter = {
    lat: -1.2921, // Nairobi
    lng: 36.8219
  };

  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');
  const [selectedBus, setSelectedBus] = useState(null);
  const mapRef = useRef(null);
  const [busMarkers, setBusMarkers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const busesPerPage = 10;
  const [selectedBuses, setSelectedBuses] = useState([]); // Array of vehicle IDs
  const [filterIgnition, setFilterIgnition] = useState('all');
  const [filterEventName, setFilterEventName] = useState('all');
  const [filterDriverDivision, setFilterDriverDivision] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBusDropdown, setShowBusDropdown] = useState(false);
  const busDropdownRef = useRef(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [hasInitialFit, setHasInitialFit] = useState(false);
  const [showHistoryTrail, setShowHistoryTrail] = useState(false);
  const [historyWindowHours, setHistoryWindowHours] = useState(3);
  const [historyTrailPoints, setHistoryTrailPoints] = useState([]);
  const [historyEventMarkers, setHistoryEventMarkers] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyVisiblePoints, setHistoryVisiblePoints] = useState(0);
  const [eventRadarCounts, setEventRadarCounts] = useState(() => getEmptyEventCounts());
  const [eventRadarData, setEventRadarData] = useState(() => createRadarDataFromCounts(getEmptyEventCounts()));
  const [eventRadarLoading, setEventRadarLoading] = useState(false);
  const [speedHistogramData, setSpeedHistogramData] = useState([]);
  const [speedHistogramLoading, setSpeedHistogramLoading] = useState(false);
  const isUpdatingMapRef = useRef(false);
  // Use stable initial values - these won't change after first render
  const [mapCenter] = useState(() => defaultCenter);
  const [mapZoom] = useState(() => 10);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  // Fetch user profiles for drivers
  const fetchUserProfiles = useCallback(async (userIds) => {
    if (userIds.size === 0) return {};
    
    try {
      const profiles = {};
      await Promise.all(Array.from(userIds).map(async (userId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            profiles[userId] = userDoc.data();
          }
        } catch (err) {
          console.error(`Error fetching user ${userId}:`, err);
        }
      }));
      return profiles;
    } catch (err) {
      console.error('Error fetching user profiles:', err);
      return {};
    }
  }, []);

  // Helper function to process data and create markers
  const processDataToMarkers = useCallback(async (devicesList, vehiclesList, routesList) => {
    // Create a map of device ID to vehicle
    const deviceToVehicle = {};
    vehiclesList.forEach(vehicle => {
      if (vehicle.assignedDevice && vehicle.assignedDevice.id) {
        deviceToVehicle[vehicle.assignedDevice.id] = vehicle;
      }
    });

    // Create a map of busId to active routes
    const busToRoute = {};
    routesList.forEach(route => {
      if (route.busId) {
        busToRoute[route.busId] = route;
      }
    });

    // Fetch roster data for routes that have rosterId
    const rosterIds = new Set();
    routesList.forEach(route => {
      if (route.rosterId) {
        rosterIds.add(route.rosterId);
      }
    });

    const rostersMap = {};
    if (rosterIds.size > 0) {
      try {
        await Promise.all(Array.from(rosterIds).map(async (rosterId) => {
          try {
            const rosterDoc = await getDoc(doc(db, 'rosters', rosterId));
            if (rosterDoc.exists()) {
              rostersMap[rosterId] = { id: rosterDoc.id, ...rosterDoc.data() };
            }
          } catch (err) {
            console.error(`Error fetching roster ${rosterId}:`, err);
          }
        }));
      } catch (err) {
        console.error('Error fetching rosters:', err);
      }
    }

    // Collect user IDs for drivers
    const userIds = new Set();
    vehiclesList.forEach(vehicle => {
      if (vehicle.assignedDriver && vehicle.assignedDriver.id) {
        userIds.add(vehicle.assignedDriver.id);
      }
    });

    // Fetch user profiles
    const profiles = await fetchUserProfiles(userIds);

    // Create bus markers with vehicle info
    const markers = devicesList
      .filter(device => {
        // Match device by document ID and ensure valid coordinates
        const lat = parseFloat(device.lat);
        const lon = parseFloat(device.lon);
        return deviceToVehicle[device.id] && !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0;
      })
      .map(device => {
        const vehicle = deviceToVehicle[device.id];
        const activeRoute = busToRoute[vehicle.id] || null;
        const driver = vehicle.assignedDriver || null;
        const driverProfile = driver && driver.id ? profiles[driver.id] : null;
        
        // Get roster data for this route if available
        const roster = activeRoute && activeRoute.rosterId ? rostersMap[activeRoute.rosterId] : null;
        
        const lat = parseFloat(device.lat);
        const lon = parseFloat(device.lon);
        
        return {
          deviceId: device.id,
          vehicleId: vehicle.id,
          position: { lat, lng: lon },
          deviceName: device.deviceName || device.imeiNumber || 'Unknown Device',
          fleetNumber: vehicle.fleetNumber || vehicle.registrationNumber || 'Unknown Fleet Number',
          vehicleName: vehicle.registrationNumber || vehicle.fleetNumber || 'Unknown Bus',
          vehicleMake: vehicle.make || '',
          vehicleModel: vehicle.model || '',
          speed: device.vehicleSpeed || 0,
          ignition: device.isIgnitionOn || false,
          battery: device.battery || 0,
          location: device.location || 'Unknown',
          eventName: device.eventName || 'Position Report',
          lastUpdated: device.lastUpdated || device.gpsDateTime || new Date().toISOString(),
          driver: driver ? {
            id: driver.id,
            name: driver.name || (driverProfile ? `${driverProfile.firstName || ''} ${driverProfile.lastName || ''}`.trim() : 'Unknown'),
            phone: driver.phone || driverProfile?.phoneNumber || 'N/A',
            division: driver.division || driverProfile?.division || ''
          } : null,
          activeRoute: activeRoute && isTripInSession(activeRoute, roster) ? {
            id: activeRoute.id,
            name: activeRoute.name || 'Unnamed Route',
            tripType: activeRoute.tripType || 'AM',
            time: activeRoute.time || 'N/A',
            rosterName: activeRoute.rosterName || 'N/A',
            startLocation: activeRoute.startLocation?.placeName || 'N/A',
            endLocation: activeRoute.endLocation?.placeName || 'N/A',
            duration: activeRoute.routeMetrics?.duration || 'N/A',
            distance: activeRoute.routeMetrics?.distance || 'N/A'
          } : null
        };
      });

    setBusMarkers(markers);
  }, [fetchUserProfiles]);

  // Set up real-time listeners for devices and vehicles
  useEffect(() => {
    if (!isLoaded) return;

    setLoading(true);
    let devicesUnsubscribe = null;
    let vehiclesUnsubscribe = null;
    let routesUnsubscribe = null;
    let initialLoadComplete = false;

    // Store current data
    let currentDevices = [];
    let currentVehicles = [];
    let currentRoutes = [];

    // Function to update markers when data changes
    const updateMarkers = async () => {
      if (currentDevices.length > 0 || currentVehicles.length > 0) {
        await processDataToMarkers(currentDevices, currentVehicles, currentRoutes);
        if (!initialLoadComplete) {
          setLoading(false);
          initialLoadComplete = true;
        }
      }
    };

    // Set up real-time listener for devices
    devicesUnsubscribe = onSnapshot(
      collection(db, 'devices'),
      (snapshot) => {
        currentDevices = [];
        snapshot.forEach(doc => {
          const deviceData = { id: doc.id, ...doc.data() };
          currentDevices.push(deviceData);
        });
        updateMarkers();
      },
      (error) => {
        console.error('Error listening to devices:', error);
        setError('Failed to load devices');
        setLoading(false);
      }
    );

    // Set up real-time listener for vehicles
    vehiclesUnsubscribe = onSnapshot(
      collection(db, 'vehicles'),
      (snapshot) => {
        currentVehicles = [];
        snapshot.forEach(doc => {
          const vehicleData = { id: doc.id, ...doc.data() };
          currentVehicles.push(vehicleData);
        });
        updateMarkers();
      },
      (error) => {
        console.error('Error listening to vehicles:', error);
        setError('Failed to load vehicles');
        setLoading(false);
      }
    );

    // Set up listener for optimized routes (less frequent updates, so we can use getDocs with polling)
    const loadRoutes = async () => {
      try {
        const routesSnapshot = await getDocs(query(
          collection(db, 'optimizedRoutes'),
          where('status', '==', 'active')
        ));
        currentRoutes = [];
        routesSnapshot.forEach(doc => {
          currentRoutes.push({ id: doc.id, ...doc.data() });
        });
        updateMarkers();
      } catch (error) {
        console.error('Error fetching routes:', error);
      }
    };

    // Load routes initially and then every 60 seconds (routes change less frequently)
    loadRoutes();
    const routesInterval = setInterval(loadRoutes, 60000);

    // Cleanup function
    return () => {
      if (devicesUnsubscribe) devicesUnsubscribe();
      if (vehiclesUnsubscribe) vehiclesUnsubscribe();
      if (routesUnsubscribe) routesUnsubscribe();
      clearInterval(routesInterval);
    };
  }, [isLoaded, processDataToMarkers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (busDropdownRef.current && !busDropdownRef.current.contains(event.target)) {
        setShowBusDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // Filter bus markers based on selected filters
  const filteredBusMarkers = busMarkers.filter(marker => {
    // Filter by selected buses
    if (selectedBuses.length > 0 && !selectedBuses.includes(marker.vehicleId)) {
      return false;
    }
    
    // Filter by ignition
    if (filterIgnition !== 'all') {
      if (filterIgnition === 'yes' && !marker.ignition) {
        return false;
      }
      if (filterIgnition === 'no' && marker.ignition) {
        return false;
      }
    }
    
    // Filter by event name
    if (filterEventName !== 'all' && marker.eventName !== filterEventName) {
      return false;
    }

    // Filter by driver division
    if (filterDriverDivision !== 'all' && (marker.driver?.division || '') !== filterDriverDivision) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        marker.vehicleName.toLowerCase().includes(searchLower) ||
        marker.deviceName.toLowerCase().includes(searchLower) ||
        (marker.driver && marker.driver.name.toLowerCase().includes(searchLower)) ||
        (marker.activeRoute && marker.activeRoute.name.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) {
        return false;
      }
    }
    
    return true;
  });

  // Get unique bus list for dropdown
  const uniqueBuses = busMarkers.reduce((acc, marker) => {
    if (!acc.find(b => b.vehicleId === marker.vehicleId)) {
      acc.push({
        vehicleId: marker.vehicleId,
        vehicleName: marker.vehicleName,
          fleetNumber: marker.fleetNumber
      });
    }
    return acc;
  }, []);

  // Get unique event names for filter
  const uniqueEventNames = [...new Set(busMarkers.map(m => m.eventName).filter(Boolean))].sort();

  // Get unique driver divisions for filter
  const uniqueDriverDivisions = [...new Set(
    busMarkers
      .map(marker => marker.driver?.division)
      .filter(Boolean)
  )].sort();

  const handleBusToggle = (vehicleId) => {
    setSelectedBuses(prev => {
      if (prev.includes(vehicleId)) {
        return prev.filter(id => id !== vehicleId);
      } else {
        return [...prev, vehicleId];
      }
    });
  };

  const handleSelectAllBuses = () => {
    if (selectedBuses.length === uniqueBuses.length) {
      setSelectedBuses([]);
    } else {
      setSelectedBuses(uniqueBuses.map(b => b.vehicleId));
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBuses, filterIgnition, filterEventName, filterDriverDivision, searchTerm]);

  // Fit map bounds to show all filtered buses (only on initial load or explicit call)
  const fitMapBounds = useCallback(() => {
    if (!mapRef.current || filteredBusMarkers.length === 0) return;

    isUpdatingMapRef.current = true;
    const bounds = new window.google.maps.LatLngBounds();
    filteredBusMarkers.forEach(marker => {
      bounds.extend(marker.position);
    });

    mapRef.current.fitBounds(bounds);
    
    // Don't zoom in too much if there's only one marker
    if (filteredBusMarkers.length === 1) {
      mapRef.current.setZoom(15);
    }
    
    // Reset flag after a short delay to allow map to update
    setTimeout(() => {
      isUpdatingMapRef.current = false;
    }, 100);
  }, [filteredBusMarkers]);

  // Only fit bounds on initial load, not on every marker update
  useEffect(() => {
    if (isLoaded && filteredBusMarkers.length > 0 && !hasInitialFit) {
      // Small delay to ensure map is fully loaded
      const timer = setTimeout(() => {
        fitMapBounds();
        setHasInitialFit(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, filteredBusMarkers.length, hasInitialFit, fitMapBounds]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Only fit bounds on initial load if we haven't already
    if (filteredBusMarkers.length > 0 && !hasInitialFit) {
      setTimeout(() => {
        fitMapBounds();
        setHasInitialFit(true);
      }, 100);
    }
  }, [filteredBusMarkers.length, hasInitialFit, fitMapBounds]);

  const handleMarkerClick = (marker) => {
    setSelectedBus(marker);
  };

  // Reset history visibility and data when changing selected vehicle
  useEffect(() => {
    setShowHistoryTrail(false);
    setHistoryTrailPoints([]);
    setHistoryEventMarkers([]);
    setHistoryVisiblePoints(0);
  }, [selectedBus?.vehicleId]);

  // Load selected vehicle speed distribution for the selected time window (histogram)
  useEffect(() => {
    const loadSpeedHistogramData = async () => {
      if (!selectedBus?.vehicleId) {
        setSpeedHistogramData([]);
        return;
      }

      setSpeedHistogramLoading(true);
      try {
        const now = Date.now();
        const windowStartIso = new Date(now - (historyWindowHours * 60 * 60 * 1000)).toISOString();
        const speedQuery = query(
          collection(db, 'events'),
          where('vehicleId', '==', selectedBus.vehicleId),
          where('lastUpdated', '>=', windowStartIso)
        );

        const snapshot = await getDocs(speedQuery);
        const speedEvents = [];

        snapshot.forEach((eventDoc) => {
          const data = eventDoc.data();
          const speed = parseFloat(data.vehicleSpeed);
          const timestampMs = toTimestampMs(data.lastUpdated);
          if (!Number.isNaN(speed) && speed >= 0 && timestampMs > 0) {
            speedEvents.push({ speed, timestampMs });
          }
        });

        setSpeedHistogramData(createSpeedHistogramData(speedEvents));
      } catch (error) {
        console.error('Error loading speed histogram data:', error);
        setSpeedHistogramData([]);
      } finally {
        setSpeedHistogramLoading(false);
      }
    };

    loadSpeedHistogramData();
  }, [selectedBus?.vehicleId, historyWindowHours]);

  // Load selected vehicle event counts for the selected time window (radar chart)
  useEffect(() => {
    const loadEventRadarData = async () => {
      if (!selectedBus?.vehicleId) {
        const emptyCounts = getEmptyEventCounts();
        setEventRadarCounts(emptyCounts);
        setEventRadarData(createRadarDataFromCounts(emptyCounts));
        return;
      }

      setEventRadarLoading(true);
      try {
        const now = Date.now();
        const windowStartIso = new Date(now - (historyWindowHours * 60 * 60 * 1000)).toISOString();
        const eventsQuery = query(
          collection(db, 'events'),
          where('vehicleId', '==', selectedBus.vehicleId),
          where('lastUpdated', '>=', windowStartIso),
          orderBy('lastUpdated', 'asc')
        );

        const eventsSnapshot = await getDocs(eventsQuery);
        const eventCounts = getEmptyEventCounts();
        const unmatchedEventSamples = {};
        const overspeedStreak = [];
        const flushOverspeedStreak = () => {
          if (overspeedStreak.length === 0) return;

          const movedAcrossCoordinates = overspeedStreak.some((point, index, arr) => {
            if (index === 0) return false;
            const previousPoint = arr[index - 1];
            if (!previousPoint.hasValidCoordinate || !point.hasValidCoordinate) return false;
            return hasMeaningfulMovement(previousPoint, point);
          });

          if (overspeedStreak.length >= 2 && movedAcrossCoordinates) {
            eventCounts.sustained_speeding += 1;
          } else {
            eventCounts.overspeeding += overspeedStreak.length;
          }

          overspeedStreak.length = 0;
        };

        eventsSnapshot.forEach((eventDoc) => {
          const data = eventDoc.data();
          const eventName = String(data.eventName || '').trim();
          const radarKey = mapEventNameToRadarKey(eventName);

          if (radarKey === 'overspeeding') {
            const lat = parseFloat(data.lat);
            const lon = parseFloat(data.lon);
            const hasValidCoordinate = Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
            overspeedStreak.push({
              hasValidCoordinate,
              position: {
                lat: Number.isNaN(lat) ? 0 : lat,
                lng: Number.isNaN(lon) ? 0 : lon
              }
            });
            return;
          }

          flushOverspeedStreak();

          if (radarKey && Object.prototype.hasOwnProperty.call(eventCounts, radarKey)) {
            eventCounts[radarKey] += 1;
          } else if (eventName) {
            unmatchedEventSamples[eventName] = (unmatchedEventSamples[eventName] || 0) + 1;
          }
        });

        flushOverspeedStreak();

        if (process.env.NODE_ENV !== 'production' && Object.keys(unmatchedEventSamples).length > 0) {
          console.debug('[Track] Unmapped event names for radar:', unmatchedEventSamples);
        }

        setEventRadarCounts(eventCounts);
        setEventRadarData(createRadarDataFromCounts(eventCounts));
      } catch (error) {
        console.error('Error loading event radar data:', error);
        const emptyCounts = getEmptyEventCounts();
        setEventRadarCounts(emptyCounts);
        setEventRadarData(createRadarDataFromCounts(emptyCounts));
      } finally {
        setEventRadarLoading(false);
      }
    };

    loadEventRadarData();
  }, [selectedBus?.vehicleId, historyWindowHours]);

  // Load and plot the selected vehicle movement history (selected window)
  useEffect(() => {
    const loadVehicleHistory = async () => {
      if (!selectedBus?.vehicleId || !showHistoryTrail) {
        setHistoryTrailPoints([]);
        setHistoryEventMarkers([]);
        return;
      }

      setHistoryLoading(true);
      try {
        const now = Date.now();
        const windowStartIso = new Date(now - (historyWindowHours * 60 * 60 * 1000)).toISOString();
        const eventsQuery = query(
          collection(db, 'events'),
          where('vehicleId', '==', selectedBus.vehicleId),
          where('lastUpdated', '>=', windowStartIso),
          orderBy('lastUpdated', 'asc')
        );

        const eventsSnapshot = await getDocs(eventsQuery);
        const rawPoints = [];

        eventsSnapshot.forEach((eventDoc) => {
          const data = eventDoc.data();
          const lat = parseFloat(data.lat);
          const lon = parseFloat(data.lon);
          if (isNaN(lat) || isNaN(lon) || (lat === 0 && lon === 0)) return;

          const eventName = String(data.eventName || '').trim();

          const point = {
            id: eventDoc.id,
            position: { lat, lng: lon },
            eventName,
            lastUpdated: data.lastUpdated,
            timestampMs: toTimestampMs(data.lastUpdated),
            speedKph: Number.isFinite(parseFloat(data.vehicleSpeed))
              ? Math.round(parseFloat(data.vehicleSpeed))
              : null
          };

          rawPoints.push(point);
        });

        // Enforce strict chronological order and remove noisy duplicates
        const sortedPoints = rawPoints
          .sort((a, b) => a.timestampMs - b.timestampMs)
          .filter((point, index, arr) => index === 0 || hasMeaningfulMovement(arr[index - 1], point));

        const eventPins = [];
        sortedPoints.forEach((point) => {
          const eventNameLower = point.eventName.toLowerCase();
          const isIgnitionOnEvent = eventNameLower.includes('ignition switched on');
          const isIgnitionOffEvent = eventNameLower.includes('ignition switched off');
          const isOverspeedEvent = eventNameLower.includes('overspeed');

          if (isIgnitionOnEvent || isIgnitionOffEvent || isOverspeedEvent) {
            let color = '#6b7280';
            if (isIgnitionOnEvent) color = '#16a34a';
            if (isIgnitionOffEvent) color = '#6b7280';
            if (isOverspeedEvent) color = '#dc2626';
            const baseTypeLabel = isIgnitionOnEvent ? 'ON' : isIgnitionOffEvent ? 'OFF' : 'OS';
            const markerLabel = isOverspeedEvent && point.speedKph !== null
              ? `${baseTypeLabel} ${point.speedKph}`
              : baseTypeLabel;
            const markerTitle = point.speedKph !== null
              ? `${point.eventName} (${point.speedKph} km/h) - ${new Date(point.lastUpdated).toLocaleString()}`
              : `${point.eventName} - ${new Date(point.lastUpdated).toLocaleString()}`;

            eventPins.push({
              ...point,
              color,
              typeLabel: markerLabel,
              markerTitle
            });
          }
        });

        setHistoryTrailPoints(sortedPoints);
        setHistoryEventMarkers(eventPins);
      } catch (error) {
        console.error('Error loading vehicle history events:', error);
        setHistoryTrailPoints([]);
        setHistoryEventMarkers([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadVehicleHistory();
  }, [selectedBus?.vehicleId, showHistoryTrail, historyWindowHours]);

  // Focus map bounds on the plotted history route when enabled
  useEffect(() => {
    if (!showHistoryTrail || !mapRef.current || historyTrailPoints.length === 0 || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    historyTrailPoints.forEach((point) => bounds.extend(point.position));
    mapRef.current.fitBounds(bounds);
    if (historyTrailPoints.length === 1) {
      mapRef.current.setZoom(16);
    }
  }, [showHistoryTrail, historyTrailPoints]);

  // Reveal history markers progressively from start to end, then stop.
  useEffect(() => {
    if (!showHistoryTrail || historyTrailPoints.length === 0) {
      setHistoryVisiblePoints(0);
      return undefined;
    }

    setHistoryVisiblePoints(1);
    const timer = setInterval(() => {
      setHistoryVisiblePoints((prev) => {
        if (prev >= historyTrailPoints.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 450);

    return () => clearInterval(timer);
  }, [showHistoryTrail, historyTrailPoints.length]);

  // Create bus icon once when map is loaded
  const [busIcon, setBusIcon] = useState(null);
  
  useEffect(() => {
    if (isLoaded && window.google && window.google.maps) {
      setBusIcon(createBusIcon(32)); // Small size - 32px, dark Font Awesome bus icon
    }
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#f6f7fb] pt-8 pb-8">
      {/* Top Bar */}
      <div className="px-8 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-black">Bus Tracking</h1>
          <div className="flex items-center gap-2">
            <FaBus className="text-yellow-500" />
            <span className="font-semibold text-gray-700">
              Active Buses: <span className="text-blue-600">{filteredBusMarkers.length}</span>
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search buses, drivers, routes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>

          {/* Bus Multi-Select Dropdown */}
          <div className="relative" ref={busDropdownRef}>
            <button
              onClick={() => setShowBusDropdown(!showBusDropdown)}
              className="w-64 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between"
            >
              <span className="text-gray-700">
                {selectedBuses.length === 0
                  ? 'All Buses'
                  : selectedBuses.length === 1
                  ? `${selectedBuses.length} Bus Selected`
                  : `${selectedBuses.length} Buses Selected`}
              </span>
              <FaChevronDown className={`text-gray-400 transition-transform ${showBusDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showBusDropdown && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                <div className="p-2 border-b border-gray-200">
                  <button
                    onClick={handleSelectAllBuses}
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                  >
                    {selectedBuses.length === uniqueBuses.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="p-2">
                  {uniqueBuses.map(bus => (
                    <label
                      key={bus.vehicleId}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBuses.includes(bus.vehicleId)}
                        onChange={() => handleBusToggle(bus.vehicleId)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{bus.vehicleName}</span>
                    </label>
                  ))}
                </div>
                {selectedBuses.length > 0 && (
                  <div className="p-2 border-t border-gray-200">
                    <button
                      onClick={() => setSelectedBuses([])}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2"
                    >
                      <FaTimes />
                      Clear Selection
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Ignition Filter */}
          <div className="w-48">
            <select
              value={filterIgnition}
              onChange={(e) => setFilterIgnition(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Ignition</option>
              <option value="yes">ON</option>
              <option value="no">OFF</option>
            </select>
          </div>

          {/* Event Name Filter */}
          <div className="w-48">
            <select
              value={filterEventName}
              onChange={(e) => setFilterEventName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Events</option>
              {uniqueEventNames.map(eventName => (
                <option key={eventName} value={eventName}>{eventName}</option>
              ))}
            </select>
          </div>

          {/* Driver Division Filter */}
          <div className="w-56">
            <select
              value={filterDriverDivision}
              onChange={(e) => setFilterDriverDivision(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Divisions</option>
              {uniqueDriverDivisions.map(division => (
                <option key={division} value={division}>{division}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 w-full px-6 pt-6" style={{background: '#f6f7fb'}}>
        {/* Map Section */}
        <div className="h-full relative" style={{width: '80%', minWidth: 0}}>
          <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
            onLoad={onMapLoad}
            options={{
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
              zoomControl: true,
              gestureHandling: 'greedy',
              maxZoom: 18,
              minZoom: 3,
            }}
          >
            {showHistoryTrail ? null : filteredBusMarkers.map((marker, index) => (
              <Marker
                key={`${marker.deviceId}-${index}`}
                position={marker.position}
                icon={busIcon}
                onClick={() => handleMarkerClick(marker)}
                title={`${marker.vehicleName} - ${marker.deviceName}`}
              />
            ))}
            {(() => {
              const visibleHistoryPoints = showHistoryTrail
                ? historyTrailPoints.slice(0, historyVisiblePoints)
                : [];
              const visiblePointIds = new Set(visibleHistoryPoints.map((point) => point.id));

              return (
                <>
                  {showHistoryTrail && visibleHistoryPoints.map((point, index) => {
                    const nextPoint = visibleHistoryPoints[index + 1]?.position;
                    const prevPoint = visibleHistoryPoints[index - 1]?.position;
                    const totalPoints = visibleHistoryPoints.length;
                    const isLast = index === totalPoints - 1;
                    const rotation = nextPoint
                      ? calculateBearing(point.position, nextPoint)
                      : (prevPoint ? calculateBearing(prevPoint, point.position) : 0);
                    const labelStepInterval = Math.max(1, Math.ceil(totalPoints / 18));
                    const showStepLabel = index === 0 || isLast || index % labelStepInterval === 0;

                    return (
                      <Marker
                        key={`history-arrow-${point.id}-${index}`}
                        position={point.position}
                        label={showStepLabel ? {
                          text: `${index + 1}`,
                          color: '#1f2937',
                          fontWeight: '700',
                          fontSize: '9px'
                        } : undefined}
                        icon={{
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          fillColor: '#60a5fa',
                          fillOpacity: 0.9,
                          strokeColor: '#1e3a8a',
                          strokeWeight: 1,
                          scale: 4.5,
                          rotation
                        }}
                        title={`Step ${index + 1} of ${totalPoints} - ${new Date(point.lastUpdated).toLocaleString()}`}
                      />
                    );
                  })}
                  {showHistoryTrail && visibleHistoryPoints[0] && (
                    <Marker
                      key="history-start"
                      position={visibleHistoryPoints[0].position}
                      label={{ text: 'S', color: '#ffffff', fontWeight: '700' }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: '#1d4ed8',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                        scale: 8
                      }}
                      title={`Start - ${new Date(visibleHistoryPoints[0].lastUpdated).toLocaleString()}`}
                    />
                  )}
                  {showHistoryTrail && visibleHistoryPoints.length > 1 && (
                    <Marker
                      key="history-end"
                      position={visibleHistoryPoints[visibleHistoryPoints.length - 1].position}
                      label={{ text: 'E', color: '#ffffff', fontWeight: '700' }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: '#7c3aed',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                        scale: 8
                      }}
                      title={`Current End - ${new Date(visibleHistoryPoints[visibleHistoryPoints.length - 1].lastUpdated).toLocaleString()}`}
                    />
                  )}
                  {showHistoryTrail && historyEventMarkers
                    .filter((eventMarker) => visiblePointIds.has(eventMarker.id))
                    .map((eventMarker) => (
                      <Marker
                        key={`history-${eventMarker.id}`}
                        position={eventMarker.position}
                        label={{ text: eventMarker.typeLabel, color: '#ffffff', fontWeight: '700', fontSize: '10px' }}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          fillColor: eventMarker.color,
                          fillOpacity: 1,
                          strokeColor: '#ffffff',
                          strokeWeight: 1,
                          scale: 6
                        }}
                        title={eventMarker.markerTitle || `${eventMarker.eventName} - ${new Date(eventMarker.lastUpdated).toLocaleString()}`}
                      />
                    ))}
                </>
              );
            })()}
            {showTraffic && <TrafficLayer />}
          </GoogleMap>

          {/* Traffic Toggle Button */}
          <div className="absolute bottom-4 left-4 z-10">
            <button
              onClick={() => setShowTraffic(!showTraffic)}
              className={`px-4 py-2 rounded-lg shadow-md font-medium transition-colors ${
                showTraffic 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {showTraffic ? 'Hide Traffic' : 'Show Traffic'}
            </button>
          </div>

          {/* Info Panel Overlay */}
          {selectedBus && (
            <div className="absolute top-20 right-4 bg-white rounded-lg shadow-lg p-4 z-10 min-w-[360px] w-[30vw] max-w-[520px]">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <FaBus className="text-yellow-500" />
                  {selectedBus.vehicleName}
                </h3>
                <button
                  onClick={() => setSelectedBus(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Fleet Number:</span>
                  <span className="ml-2 text-gray-600">{selectedBus.fleetNumber}</span>
                </div>
                
                {selectedBus.vehicleMake && (
                  <div>
                    <span className="font-semibold text-gray-700">Vehicle:</span>
                    <span className="ml-2 text-gray-600">
                      {selectedBus.vehicleMake} {selectedBus.vehicleModel}
                    </span>
                  </div>
                )}

                {/* Driver Information */}
                {selectedBus.driver && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUser className="text-gray-400" />
                      <span className="font-semibold text-gray-700">Driver:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <div>
                        <span className="text-gray-600">{selectedBus.driver.name}</span>
                      </div>
                      {selectedBus.driver.phone && (
                        <div className="text-xs text-gray-500">
                          Phone: {selectedBus.driver.phone}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Active Trip Information */}
                {selectedBus.activeRoute && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <FaRoute className="text-blue-500" />
                      <span className="font-semibold text-gray-700">Active Trip:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <div>
                        <span className="text-gray-600 font-medium">{selectedBus.activeRoute.name}</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Type:</span> {selectedBus.activeRoute.tripType === 'AM' ? 'School Trip (AM)' : selectedBus.activeRoute.tripType === 'PM' ? 'Home Trip (PM)' : selectedBus.activeRoute.tripType}
                      </div>
                      {selectedBus.activeRoute.time && (
                        <div className="text-xs text-gray-600">
                          <FaClock className="inline mr-1" />
                          <span className="font-medium">Time:</span> {selectedBus.activeRoute.time}
                        </div>
                      )}
                      {selectedBus.activeRoute.rosterName && (
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Roster:</span> {selectedBus.activeRoute.rosterName}
                        </div>
                      )}
                      <div className="text-xs text-gray-600">
                        <FaMapMarkerAlt className="inline mr-1" />
                        <span className="font-medium">From:</span> {selectedBus.activeRoute.startLocation}
                      </div>
                      <div className="text-xs text-gray-600">
                        <FaMapMarkerAlt className="inline mr-1" />
                        <span className="font-medium">To:</span> {selectedBus.activeRoute.endLocation}
                      </div>
                      {selectedBus.activeRoute.duration && (
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Duration:</span> {selectedBus.activeRoute.duration}
                        </div>
                      )}
                      {selectedBus.activeRoute.distance && (
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Distance:</span> {selectedBus.activeRoute.distance}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <FaTachometerAlt className="text-gray-400" />
                    <span className="font-semibold text-gray-700">Speed:</span>
                    <span className="ml-2 text-gray-600">{selectedBus.speed} km/h</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <FaBatteryFull className="text-gray-400" />
                    <span className="font-semibold text-gray-700">Battery:</span>
                    <span className="ml-2 text-gray-600">{selectedBus.battery}%</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-3 h-3 rounded-full ${selectedBus.ignition ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    <span className="font-semibold text-gray-700">Ignition:</span>
                    <span className={`ml-2 ${selectedBus.ignition ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedBus.ignition ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <FaMapMarkerAlt className="text-gray-400" />
                    <span className="font-semibold text-gray-700">Location:</span>
                    <span className="ml-2 text-gray-600 text-xs">{selectedBus.location}</span>
                  </div>

                  {selectedBus.eventName && (
                    <div className="mt-2">
                      <span className="font-semibold text-gray-700">Event:</span>
                      <span className="ml-2 text-gray-600 text-xs">{selectedBus.eventName}</span>
                    </div>
                  )}

                  <div className="mt-3 pt-2 border-t">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showHistoryTrail}
                        onChange={(e) => setShowHistoryTrail(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Show {historyWindowHours}-hour movement history
                      </span>
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {historyWindowOptions.map((hours) => (
                        <button
                          key={`window-${hours}`}
                          type="button"
                          onClick={() => setHistoryWindowHours(hours)}
                          className={`px-2 py-1 rounded-md border text-[10px] font-semibold transition-colors ${
                            historyWindowHours === hours
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {hours}h
                        </button>
                      ))}
                    </div>
                    {showHistoryTrail && (
                      <p className="text-xs text-gray-500 mt-1">
                        Plotting route + ignition on/off + overspeed events
                        {historyLoading ? ' (loading...)' : ` (${historyTrailPoints.length} points)`}
                      </p>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">{historyWindowHours}-hour driving event radar</span>
                      {eventRadarLoading && <span className="text-xs text-gray-500">Loading...</span>}
                    </div>
                    <div className="w-full h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={eventRadarData} margin={{ top: 6, right: 10, left: 10, bottom: 0 }}>
                          <PolarGrid stroke="#e5e7eb" />
                          <PolarAngleAxis dataKey="event" tick={{ fill: '#6b7280', fontSize: 10 }} />
                          <PolarRadiusAxis allowDecimals={false} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                          <Tooltip />
                          <Radar
                            name="Event profile"
                            dataKey="value"
                            stroke="#6366f1"
                            fill="#818cf8"
                            fillOpacity={0.35}
                            strokeWidth={2}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {trackedRadarEvents.map((eventItem) => (
                        <div
                          key={`badge-${eventItem.key}`}
                          className="flex items-center justify-between rounded-md border border-gray-200 px-2 py-1"
                        >
                          <span className="flex items-center gap-1 text-[10px] text-gray-700 pr-2">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: eventItem.color }}
                            />
                            {eventItem.label}
                          </span>
                          <span className="text-[10px] font-semibold text-gray-800 shrink-0 min-w-[16px] text-right">
                            {eventRadarCounts[eventItem.key] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">{historyWindowHours}-hour speed over time</span>
                      {speedHistogramLoading && <span className="text-xs text-gray-500">Loading...</span>}
                    </div>
                    <div className="w-full h-52">
                      {speedHistogramData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-gray-500 border border-gray-200 rounded-md">
                          No speed readings in the last {historyWindowHours} hours
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={speedHistogramData} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis
                              dataKey="timeLabel"
                              tick={{ fill: '#6b7280', fontSize: 10 }}
                              interval="preserveStartEnd"
                              minTickGap={24}
                            />
                            <YAxis allowDecimals={false} tick={{ fill: '#6b7280', fontSize: 10 }} />
                            <Tooltip formatter={(value) => [`${value} km/h`, 'Speed']} />
                            <Bar dataKey="avgSpeed" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <span className="text-xs text-gray-500">
                    Last Update: {new Date(selectedBus.lastUpdated).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar with Bus List */}
        <div className="h-full bg-white p-4 overflow-y-auto" style={{width: '20%', minWidth: 0}}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaBus className="text-yellow-500" />
                Bus List
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {loading ? 'Loading...' : `${filteredBusMarkers.length} bus${filteredBusMarkers.length !== 1 ? 'es' : ''} on map`}
              </p>
            </div>
          </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredBusMarkers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FaBus className="text-4xl mx-auto mb-4 text-gray-300" />
            <p>No buses match the selected filters</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {filteredBusMarkers
                .slice((currentPage - 1) * busesPerPage, currentPage * busesPerPage)
                .map((marker, index) => (
                  <div
                    key={`${marker.deviceId}-${index}`}
                    onClick={() => {
                      setSelectedBus(marker);
                      if (mapRef.current) {
                        mapRef.current.setCenter(marker.position);
                        mapRef.current.setZoom(15);
                      }
                    }}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedBus?.deviceId === marker.deviceId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 border border-yellow-200 flex items-center justify-center">
                          <span className="text-xs font-bold text-yellow-800 leading-none">
                            {marker.fleetNumber}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">
                          {marker.vehicleName}
                        </h3>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <FaTachometerAlt />
                            {marker.speed} km/h
                          </span>
                          <span className={`flex items-center gap-1 ${marker.ignition ? 'text-green-600' : 'text-gray-400'}`}>
                            <span className={`w-2 h-2 rounded-full ${marker.ignition ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                            {marker.ignition ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            
            {/* Pagination Controls */}
            {filteredBusMarkers.length > busesPerPage && (
              <div className="flex justify-end items-center mt-4 space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {Math.ceil(filteredBusMarkers.length / busesPerPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredBusMarkers.length / busesPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(filteredBusMarkers.length / busesPerPage)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Track;
